import React, { useEffect, useMemo } from "react";
import {
  DatePicker,
  Form,
  Grid,
  Input,
  InputNumber,
  Modal,
  Radio,
  Switch,
  Select,
  Row,
  Col,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  ClientLocation,
  Credit,
  CreditPaymentMode,
  CreditPaymentType,
} from "./types";

type Props = {
  open: boolean;
  loading?: boolean;
  credit?: Credit | null;
  locations?: ClientLocation[];
  onCancel: () => void;
  onSubmit: (values: any) => void;
};

const { Text } = Typography;
const { useBreakpoint } = Grid;

const CreditFormModal: React.FC<Props> = ({
  open,
  loading,
  credit,
  locations = [],
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobile = !screens.md; // < 768px
  const [form] = Form.useForm();

  useEffect(() => {
    if (credit) {
      form.setFieldsValue({
        ...credit,
        startDate: credit.startDate ? dayjs(credit.startDate) : undefined,
        disbursementDate: credit.disbursementDate
          ? dayjs(credit.disbursementDate)
          : undefined,
        endDate: credit.endDate ? dayjs(credit.endDate) : undefined,
        locationId:
          typeof credit.locationId === "object"
            ? credit.locationId?._id
            : credit.locationId,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        paymentType: "INSTALLMENTS" as CreditPaymentType,
        paymentMode: "AUTO" as CreditPaymentMode,
        installmentsCount: 12,
        allowManualOverride: true,
        startDate: dayjs(),
        useBankCalculation: false,
        repaymentType: "ANNUITY",
        dayCountConvention: "THIRTY_360",
        gracePeriods: 0,
        graceType: "PAID_INTEREST",
        currency: "EUR",
      });
    }
  }, [credit, form]);

  const paymentType = Form.useWatch("paymentType", form) as
    | CreditPaymentType
    | undefined;
  const useBankCalculation = Form.useWatch("useBankCalculation", form);
  const totalAmount = Form.useWatch("totalAmount", form);
  const installmentsCount = Form.useWatch("installmentsCount", form);
  const totalInterest = Form.useWatch("totalInterest", form);
  const totalWithInterest = Form.useWatch("totalWithInterest", form);
  const startDate = Form.useWatch("startDate", form);
  const disbursementDate = Form.useWatch("disbursementDate", form);
  const endDate = Form.useWatch("endDate", form);

  // Automatski izračunaj broj rata kada se unese endDate
  // +1 jer uključujemo i početni mesec (prva rata je na disbursementDate)
  useEffect(() => {
    if (endDate && (disbursementDate || startDate) && useBankCalculation) {
      const baseDate = disbursementDate || startDate;
      if (baseDate && dayjs(endDate).isAfter(dayjs(baseDate))) {
        const months = dayjs(endDate).diff(dayjs(baseDate), "month") + 1;
        form.setFieldsValue({ installmentsCount: months });
      }
    }
  }, [endDate, disbursementDate, startDate, useBankCalculation, form]);

  const calculatedValues = useMemo(() => {
    const principal = Number(totalAmount) || 0;
    const total = Number(totalWithInterest) || principal;
    const totalInt = Number(totalInterest) || 0;

    // Izračunaj prosječnu kamatnu stopu
    const interestAmount = Math.max(0, total - principal);
    const averageRate = principal > 0 ? (interestAmount / principal) * 100 : 0;

    const monthly =
      paymentType === "INSTALLMENTS" &&
      total &&
      installmentsCount &&
      installmentsCount > 0
        ? Number((total / Number(installmentsCount)).toFixed(2))
        : total || 0;

    return {
      interestAmount: Number(interestAmount.toFixed(2)),
      averageRate: Number(averageRate.toFixed(2)),
      monthly,
    };
  }, [
    paymentType,
    totalAmount,
    installmentsCount,
    totalInterest,
    totalWithInterest,
  ]);

  return (
    <Modal
      open={open}
      title={
        credit
          ? t("Update credit plan")
          : t("Create a new credit repayment plan")
      }
      width={isMobile ? "100%" : 600}
      onCancel={onCancel}
      className="standardHeader standardFooter"
      onOk={() => form.submit()}
      okButtonProps={{ loading }}
      destroyOnClose
      styles={
        isMobile
          ? { body: { maxHeight: "70vh", overflowY: "auto" } }
          : undefined
      }
    >
      <Form layout="vertical" form={form} onFinish={onSubmit}>
        <Form.Item
          name="name"
          label={t("Credit name")}
          rules={[{ required: true, message: t("Please enter a name") }]}
        >
          <Input placeholder={t("Example: Office renovation loan")} />
        </Form.Item>
        <Form.Item name="description" label={t("Description")}>
          <Input.TextArea rows={3} placeholder={t("Internal note")} />
        </Form.Item>

        <Form.Item
          name="useBankCalculation"
          label={t("Use bank calculation")}
          tooltip={t(
            "Enable advanced amortization schedule with fees, prepayments, and interest calculation",
          )}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="totalAmount"
              label={t("Principal amount")}
              rules={[
                { required: true, message: t("Amount is required") },
                { type: "number", min: 0 },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={50}
                placeholder="0.00"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="currency"
              label={t("Currency")}
              rules={[{ required: true }]}
            >
              <Select>
                <Select.Option value="EUR">EUR</Select.Option>
                <Select.Option value="BAM">BAM</Select.Option>
                <Select.Option value="RSD">RSD</Select.Option>
                <Select.Option value="USD">USD</Select.Option>
                <Select.Option value="CHF">CHF</Select.Option>
                <Select.Option value="GBP">GBP</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="startDate"
              label={t("Loan approval date")}
              tooltip={t("Date when the loan was approved/signed")}
              rules={[{ required: true, message: t("Select approval date") }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="disbursementDate"
              label={t("Disbursement date")}
              tooltip={t(
                "Date when the loan amount was actually disbursed (paid out)",
              )}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="endDate"
              label={t("End date (last installment)")}
              tooltip={t(
                "Date of the last installment - will auto-calculate number of periods if bank calculation is enabled",
              )}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="totalInterest"
              label={t("Total interest (amount)")}
              tooltip={t("Total interest amount in currency")}
              rules={[{ type: "number", min: 0 }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={10}
                placeholder="0"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="totalWithInterest"
              label={t("Total amount with interest")}
              rules={[
                { required: true, message: t("Total amount is required") },
                { type: "number", min: 0 },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const principal = getFieldValue("totalAmount");
                    if (!value || !principal || value >= principal) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        t(
                          "Total with interest must be greater than or equal to principal amount",
                        ),
                      ),
                    );
                  },
                }),
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={50}
                placeholder="0.00"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <div className="calculatedMonthly">
              <Text type="secondary">{t("Average interest rate")}</Text>
              <div className="value">
                {calculatedValues.averageRate.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                %
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div className="calculatedMonthly">
              <Text type="secondary">{t("Interest amount")}</Text>
              <div className="value">
                {calculatedValues.interestAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <div className="calculatedMonthly">
              <Text type="secondary">
                {t("Calculated monthly installment")}
              </Text>
              <div className="value">
                {calculatedValues.monthly?.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          </Col>
        </Row>
        <Form.Item
          name="paymentType"
          label={t("Repayment type")}
          rules={[{ required: true, message: t("Select repayment type") }]}
        >
          <Radio.Group>
            <Radio.Button value="INSTALLMENTS">
              {t("Monthly installments")}
            </Radio.Button>
            <Radio.Button value="MANUAL">{t("Manual tracking")}</Radio.Button>
          </Radio.Group>
        </Form.Item>
        {paymentType === "INSTALLMENTS" && (
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="installmentsCount"
                label={t("Number of installments")}
                tooltip={
                  endDate && useBankCalculation
                    ? t("Auto-calculated from end date")
                    : undefined
                }
                rules={[
                  {
                    required: true,
                    message: t("Provide number of installments"),
                  },
                  { type: "number", min: 1 },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={1}
                  step={1}
                  placeholder="12"
                  disabled={endDate && useBankCalculation ? true : false}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {useBankCalculation && (
          <>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  name="annualInterestRate"
                  label={t("Annual interest rate (%)")}
                  tooltip={t("Nominal annual interest rate for calculation")}
                  rules={[
                    { required: true, message: t("Interest rate is required") },
                    { type: "number", min: 0, max: 100 },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    max={100}
                    step={0.1}
                    placeholder="10.0"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="effectiveAnnualRate"
                  label={t("Effective annual rate (%)")}
                  tooltip={t(
                    "EKS - includes all costs (fees, insurance, etc.)",
                  )}
                  rules={[{ type: "number", min: 0, max: 100 }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    max={100}
                    step={0.01}
                    placeholder="10.81"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  name="repaymentType"
                  label={t("Repayment method")}
                  rules={[{ required: true }]}
                >
                  <Select>
                    <Select.Option value="ANNUITY">
                      {t("Annuity (fixed installment)")}
                    </Select.Option>
                    <Select.Option value="DECLINING_BALANCE">
                      {t("Declining balance")}
                    </Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  name="dayCountConvention"
                  label={t("Day count convention")}
                  tooltip={t("Method for calculating interest based on days")}
                  rules={[{ required: true }]}
                >
                  <Select>
                    <Select.Option value="THIRTY_360">
                      {t("30/360 (Banking)")}
                    </Select.Option>
                    <Select.Option value="ACTUAL_365">
                      {t("Actual/365")}
                    </Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="gracePeriods"
                  label={t("Grace periods")}
                  tooltip={t("Number of periods with no principal repayment")}
                  rules={[{ type: "number", min: 0 }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    step={1}
                    placeholder="0"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="graceType"
              label={t("Grace period type")}
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Radio.Button value="PAID_INTEREST">
                  {t("Pay interest")}
                </Radio.Button>
                <Radio.Button value="CAPITALIZED_INTEREST">
                  {t("Capitalize interest")}
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          </>
        )}

        <Form.Item
          name="paymentMode"
          label={t("Payment handling")}
          rules={[{ required: true, message: t("Select payment mode") }]}
        >
          <Radio.Group>
            <Radio.Button value="AUTO">
              {t("Auto-generate monthly")}
            </Radio.Button>
            <Radio.Button value="MANUAL">
              {t("I will add manually")}
            </Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="allowManualOverride"
          label={t("Allow manual overrides")}
          tooltip={t(
            "Enable adding custom manual payments even for auto plans",
          )}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        {locations.length > 0 && (
          <Form.Item name="locationId" label={t("Location (optional)")}>
            <Select allowClear placeholder={t("Select location")}>
              {locations.map((loc) => (
                <Select.Option key={loc._id} value={loc._id}>
                  {loc.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default React.memo(CreditFormModal);
